import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Icon, Sheet, Stepper, Switch, cn } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  FEATURE_FLAGS,
  operatorPlanListResponseSchema,
  type OperatorPlan,
} from '@forecastify/shared';
import { useI18n, useT, type MessageKey } from '../i18n';
import { useOperatorSession } from '../session';
import { formatMoney, formatNumber } from '../chrome/format';
import { HeaderActions } from '../chrome/page-chrome';
import { useToast } from '../chrome/toasts';
import { BTN, BTN_SM, FIELD, PILL, TH, TH_ROW, TONE } from '../chrome/tones';
import { SkeletonRows } from '../chrome/skeleton-rows';
import { featureName } from './feature-names';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Csomagok* — the catalogue, and a side sheet to change it.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE REDESIGN'S TABLE AND SHEET.** Features as named chips rather than `key: Be` text, the
 * state as a pill, a withdrawn plan's whole row muted, and the editor sliding in from the right with
 * the feature switches as a list — where the first version used a centred dialog and printed flag
 * keys. The whole row opens the sheet; *Új csomag* sits in the shell's header.
 *
 * ⚠️⚠️ **WITHDRAWING IS NOT DELETING, AND THE SHEET SAYS SO AT THE MOMENT IT MATTERS.** Turning
 * *Kínálva* off keeps every tenant on the plan — `is_active` means "not offered any more" — and the
 * sheet prints how many stay on it right under the switch.
 *
 * ⚠️ **Each field's refusal sits under that field**, after a first attempt — not as a banner, and
 * not before anybody has typed.
 */

interface Draft {
  readonly name: string;
  readonly seatPrice: string;
  readonly currency: string;
  readonly seatLimit: string;
  readonly features: Readonly<Record<string, boolean>>;
  readonly isActive: boolean;
}

type Load =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly code: string }
  | { readonly state: 'ready'; readonly plans: readonly OperatorPlan[] };

export function PlansScreen(): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const toast = useToast();

  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [editing, setEditing] = useState<{ plan: OperatorPlan | null; draft: Draft } | null>(null);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const read = useCallback(async (): Promise<void> => {
    const result = await session.request<unknown>(CONTROL_PANEL_PATHS.plans);
    if (!result.ok) {
      if (result.status === 401) return;
      setLoad({ state: 'failed', code: result.code });
      return;
    }
    const parsed = operatorPlanListResponseSchema.safeParse(result.value);
    /* ⚠️ A 200 that does not match the contract is a failure, not "no plans". */
    setLoad(
      parsed.success
        ? { state: 'ready', plans: parsed.data.data }
        : { state: 'failed', code: 'unknown' },
    );
  }, [session]);

  useEffect(() => {
    void read();
  }, [read]);

  const open = (plan: OperatorPlan | null): void => {
    setTried(false);
    setFailure(null);
    setEditing({
      plan,
      draft:
        plan === null
          ? {
              name: '',
              seatPrice: '',
              currency: 'EUR',
              seatLimit: '',
              features: {},
              isActive: true,
            }
          : {
              name: plan.name,
              seatPrice: plan.seatPrice,
              currency: plan.currency,
              seatLimit: plan.seatLimit === null ? '' : String(plan.seatLimit),
              features: plan.featureFlags,
              isActive: plan.isActive,
            },
    });
  };

  const patch = (change: Partial<Draft>): void => {
    setEditing((current) =>
      current === null ? null : { ...current, draft: { ...current.draft, ...change } },
    );
  };

  const messageFor = (code: string): string => {
    const known: Readonly<Record<string, MessageKey>> = {
      duplicate_name: 'error.duplicate_name',
      unknown_feature: 'error.unknown_feature',
      conflict: 'error.conflict',
      not_found: 'error.not_found',
      validation_failed: 'error.validation_failed',
      unauthenticated: 'error.unauthenticated',
      unreachable: 'error.unreachable',
    };
    const key = known[code];
    return key === undefined ? t('error.unknown') : t(key);
  };

  const draft = editing?.draft;
  const nameOk = draft !== undefined && draft.name.trim() !== '';
  const priceOk = draft !== undefined && /^\d{1,12}([.,]\d{1,2})?$/u.test(draft.seatPrice.trim());
  const currencyOk = draft !== undefined && /^[A-Z]{3}$/u.test(draft.currency.trim().toUpperCase());

  const save = (): void => {
    if (editing === null || busy) return;
    setTried(true);
    if (!nameOk || !priceOk || !currencyOk) return;
    const { plan, draft: d } = editing;
    /* ⚠️ Empty means unlimited, which is `null` — not `0`, and not omitted. */
    const seatLimit = d.seatLimit.trim() === '' ? null : Number(d.seatLimit.trim());
    const common = {
      name: d.name.trim(),
      seatPrice: d.seatPrice.trim().replace(',', '.'),
      currency: d.currency.trim().toUpperCase(),
      seatLimit,
      featureFlags: d.features,
    };
    const body =
      plan === null ? common : { ...common, isActive: d.isActive, version: plan.version };

    setBusy(true);
    setFailure(null);
    void session
      .request<unknown>(
        plan === null ? CONTROL_PANEL_PATHS.plans : CONTROL_PANEL_PATHS.plan(plan.id),
        {
          method: plan === null ? 'POST' : 'PATCH',
          body: JSON.stringify(body),
        },
      )
      .then((result) => {
        setBusy(false);
        if (!result.ok) {
          if (result.status !== 401) setFailure(messageFor(result.code));
          return;
        }
        toast({
          title: plan === null ? t('plans.created') : t('plans.saved'),
          description: common.name,
        });
        setEditing(null);
        /* Re-read rather than splice: `tenantCount` is a fact about tenants. */
        void read();
      });
  };

  return (
    <div data-screen="plans" className="flex flex-col gap-3 p-5">
      <HeaderActions>
        <Button
          size="sm"
          className={BTN_SM}
          onClick={() => {
            open(null);
          }}
        >
          <Icon name="plus" size={14} />
          {t('plans.new')}
        </Button>
      </HeaderActions>

      {load.state === 'failed' ? (
        <div role="alert" className="flex flex-col items-start gap-2">
          <p className="text-[13px]">{t('plans.failed')}</p>
          <Button
            variant="outline"
            size="sm"
            className={BTN_SM}
            onClick={() => {
              void read();
            }}
          >
            {t('plans.retry')}
          </Button>
        </div>
      ) : load.state === 'ready' && load.plans.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-14 text-center">
          <p className="max-w-[46ch] text-[13px] text-pretty">{t('plans.empty')}</p>
          <Button
            size="sm"
            className={BTN_SM}
            onClick={() => {
              open(null);
            }}
          >
            {t('plans.new')}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table aria-label={t('plans.heading')} className="w-full min-w-[820px] text-[13px]">
            <thead className="bg-muted/40 border-b">
              <tr className={TH_ROW}>
                <th className={TH}>{t('plans.column.name')}</th>
                <th className={cn(TH, 'text-right')}>{t('plans.column.price')}</th>
                <th className={cn(TH, 'text-right')}>{t('plans.column.seats')}</th>
                <th className={TH}>{t('plans.column.features')}</th>
                <th className={TH}>{t('plans.column.state')}</th>
                <th className={cn(TH, 'text-right')}>{t('plans.column.tenants')}</th>
              </tr>
            </thead>
            <tbody>
              {load.state === 'loading' ? (
                <SkeletonRows columns={6} rowClassName="h-11" rightAligned={[1, 2, 5]} />
              ) : (
                load.plans.map((plan) => (
                  <tr
                    key={plan.id}
                    className={cn(
                      'hover:bg-muted/50 h-11 cursor-pointer border-b last:border-0',
                      plan.isActive ? '' : 'text-muted-foreground',
                    )}
                    onClick={() => {
                      open(plan);
                    }}
                  >
                    <td className="px-3">
                      <button
                        type="button"
                        className="block text-left font-medium"
                        onClick={(event) => {
                          event.stopPropagation();
                          open(plan);
                        }}
                      >
                        {plan.name}
                      </button>
                    </td>
                    <td className="px-3 text-right whitespace-nowrap tabular-nums">
                      {formatMoney(plan.seatPrice, plan.currency, locale)}
                    </td>
                    <td className="px-3 text-right tabular-nums">
                      {plan.seatLimit === null ? '∞' : formatNumber(plan.seatLimit, locale)}
                    </td>
                    <td className="px-3">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(plan.featureFlags)
                          .filter(([, on]) => on)
                          .map(([key]) => (
                            <span
                              key={key}
                              className="bg-muted rounded px-1.5 py-px text-[11.5px] whitespace-nowrap"
                            >
                              {featureName(key, t)}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td className="px-3">
                      <span className={cn(PILL, plan.isActive ? TONE.ok : TONE.muted)}>
                        {plan.isActive ? t('plans.state.active') : t('plans.state.inactive')}
                      </span>
                    </td>
                    <td className="px-3 text-right tabular-nums">
                      {formatNumber(plan.tenantCount, locale)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {load.state === 'loading' ? (
            <p role="status" className="sr-only">
              {t('plans.loading')}
            </p>
          ) : null}
        </div>
      )}

      <Sheet
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        title={
          editing?.plan == null
            ? t('plans.form.create')
            : `${t('plans.form.edit')} · ${editing.plan.name}`
        }
        width="min(520px, 94vw)"
        closeLabel={t('dialog.close')}
      >
        {draft === undefined ? null : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5">
              <label className="flex flex-col gap-1.5">
                <span className="text-[12.5px] font-medium">{t('plans.form.name')}</span>
                <input
                  className={FIELD}
                  value={draft.name}
                  onChange={(event) => {
                    patch({ name: event.target.value });
                  }}
                />
                {tried && !nameOk ? (
                  <span className="text-destructive text-[12px]">
                    {t('plans.form.nameRequired')}
                  </span>
                ) : null}
              </label>

              <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12.5px] font-medium">{t('plans.column.price')}</span>
                  <input
                    className={cn(FIELD, 'text-right tabular-nums')}
                    inputMode="decimal"
                    value={draft.seatPrice}
                    onChange={(event) => {
                      patch({ seatPrice: event.target.value });
                    }}
                  />
                  {tried && !priceOk ? (
                    <span className="text-destructive text-[12px]">
                      {t('plans.form.priceHint')}
                    </span>
                  ) : null}
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12.5px] font-medium">{t('plans.form.currency')}</span>
                  <input
                    className={cn(FIELD, 'font-mono uppercase')}
                    maxLength={3}
                    value={draft.currency}
                    onChange={(event) => {
                      patch({ currency: event.target.value.toUpperCase() });
                    }}
                  />
                  <span
                    className={cn(
                      'text-[12px]',
                      tried && !currencyOk ? 'text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {t('plans.form.currencyHint')}
                  </span>
                </label>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-[12.5px] font-medium">{t('plans.column.seats')}</span>
                <div className="flex items-center gap-3">
                  <Stepper
                    value={draft.seatLimit}
                    onChange={(value) => {
                      patch({ seatLimit: value });
                    }}
                    min={0}
                    placeholder="∞"
                    decrementLabel={t('stepper.decrement')}
                    incrementLabel={t('stepper.increment')}
                    className="bg-muted/50 hover:border-input w-[120px] border-transparent shadow-none"
                  />
                  <span className="text-[12.5px] font-medium">
                    {draft.seatLimit.trim() === ''
                      ? t('plans.unlimited')
                      : draft.seatLimit.trim() === '0'
                        ? t('plans.form.nobody')
                        : t('plans.form.seatsCount', { count: draft.seatLimit.trim() })}
                  </span>
                </div>
                <span className="text-muted-foreground text-[12px]">
                  {t('plans.form.seatsHint')}
                </span>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-[12.5px] font-medium">{t('plans.form.features')}</span>
                <div className="flex flex-col rounded-lg border">
                  {FEATURE_FLAGS.map((flag) => (
                    <div
                      key={flag.key}
                      className="flex h-10 items-center gap-3 border-b px-3 last:border-0"
                    >
                      <span className="min-w-0 flex-1 text-[13px]">{featureName(flag.key, t)}</span>
                      <span className="text-muted-foreground font-mono text-[11px]">
                        {flag.key}
                      </span>
                      <Switch
                        label={featureName(flag.key, t)}
                        checked={draft.features[flag.key] === true}
                        onCheckedChange={(next) => {
                          patch({ features: { ...draft.features, [flag.key]: next } });
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {editing?.plan == null ? null : (
                <div className="flex items-start gap-3 rounded-lg border p-3">
                  <Switch
                    label={t('plans.form.active')}
                    checked={draft.isActive}
                    onCheckedChange={(next) => {
                      patch({ isActive: next });
                    }}
                    className="mt-0.5"
                  />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[13px] font-medium">{t('plans.form.active')}</span>
                    <span className="text-muted-foreground text-[12px] text-pretty">
                      {t('plans.form.activeHint')}
                    </span>
                    {editing.plan.isActive && !draft.isActive ? (
                      <span className="text-[12px] text-[var(--status-warn-text)]">
                        {t('plans.form.withdrawWarn', { count: editing.plan.tenantCount })}
                      </span>
                    ) : null}
                  </div>
                </div>
              )}

              {failure === null ? null : (
                <p role="alert" className="text-destructive text-[12.5px]">
                  {failure}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
              <Button
                variant="ghost"
                className={BTN}
                onClick={() => {
                  setEditing(null);
                }}
              >
                {t('plans.form.cancel')}
              </Button>
              <Button className={BTN} disabled={busy} onClick={save}>
                {busy ? t('plans.form.saving') : t('plans.form.save')}
              </Button>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}
